import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "@/context/AuthContext";
import { useHost } from "@/context/HostContext";
import { TUTORIAL_MAP, TUTORIALS_LIST } from "@/lib/tutorials/basicDynicty";

const TutorialContext = createContext(null);

/**
 * Persistence key — di-scope per (tenant slug, user id) sehingga admin
 * berbeda / tenant berbeda punya progress terpisah.
 */
const buildStorageKey = (tenantSlug, userId) => {
  const t = tenantSlug || "unknown-tenant";
  const u = userId || "anonymous";
  return `dynicty_tutorial_progress::${t}::${u}`;
};

const readStore = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_e) {
    return {};
  }
};

const writeStore = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (_e) {
    /* storage full / private mode — silently ignore */
  }
};

export function TutorialProvider({ children }) {
  const { user } = useAuth();
  const { slug } = useHost();

  const storageKey = useMemo(
    () => buildStorageKey(slug, user?.id),
    [slug, user?.id]
  );

  const [progress, setProgress] = useState({});
  const [activeTutorialId, setActiveTutorialId] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const hydratedRef = useRef(false);

  // Hydrate whenever storage key changes (user login, tenant switch).
  useEffect(() => {
    setProgress(readStore(storageKey));
    hydratedRef.current = true;
    // Any active run is user-triggered, so we clear it on identity change.
    setActiveTutorialId(null);
    setCurrentStepIndex(0);
  }, [storageKey]);

  const persistProgress = useCallback(
    (next) => {
      setProgress(next);
      if (hydratedRef.current) writeStore(storageKey, next);
    },
    [storageKey]
  );

  const getProgress = useCallback(
    (tutorialId) => progress[tutorialId] || null,
    [progress]
  );

  const start = useCallback(
    (tutorialId, { resume = false } = {}) => {
      const tutorial = TUTORIAL_MAP[tutorialId];
      if (!tutorial) return;
      const saved = progress[tutorialId];
      const startIndex =
        resume && saved && Number.isInteger(saved.stepIndex)
          ? Math.min(saved.stepIndex, tutorial.steps.length - 1)
          : 0;
      setActiveTutorialId(tutorialId);
      setCurrentStepIndex(startIndex);
      const next = {
        ...progress,
        [tutorialId]: {
          stepIndex: startIndex,
          totalSteps: tutorial.steps.length,
          status: "in_progress",
          updatedAt: new Date().toISOString(),
        },
      };
      persistProgress(next);
    },
    [progress, persistProgress]
  );

  const goToStep = useCallback(
    (nextIndex) => {
      if (!activeTutorialId) return;
      const tutorial = TUTORIAL_MAP[activeTutorialId];
      if (!tutorial) return;
      const clamped = Math.max(0, Math.min(nextIndex, tutorial.steps.length - 1));
      setCurrentStepIndex(clamped);
      const next = {
        ...progress,
        [activeTutorialId]: {
          stepIndex: clamped,
          totalSteps: tutorial.steps.length,
          status: "in_progress",
          updatedAt: new Date().toISOString(),
        },
      };
      persistProgress(next);
    },
    [activeTutorialId, progress, persistProgress]
  );

  const nextStep = useCallback(() => {
    if (!activeTutorialId) return;
    const tutorial = TUTORIAL_MAP[activeTutorialId];
    if (!tutorial) return;
    if (currentStepIndex >= tutorial.steps.length - 1) {
      // Finished
      const next = {
        ...progress,
        [activeTutorialId]: {
          stepIndex: tutorial.steps.length - 1,
          totalSteps: tutorial.steps.length,
          status: "completed",
          updatedAt: new Date().toISOString(),
        },
      };
      persistProgress(next);
      setActiveTutorialId(null);
      setCurrentStepIndex(0);
      return;
    }
    goToStep(currentStepIndex + 1);
  }, [activeTutorialId, currentStepIndex, progress, persistProgress, goToStep]);

  const prevStep = useCallback(() => {
    goToStep(currentStepIndex - 1);
  }, [goToStep, currentStepIndex]);

  const stop = useCallback(() => {
    // Just close overlay; keep progress so user can resume later.
    setActiveTutorialId(null);
    setCurrentStepIndex(0);
  }, []);

  const skipTutorial = useCallback(() => {
    if (!activeTutorialId) return;
    const tutorial = TUTORIAL_MAP[activeTutorialId];
    const next = {
      ...progress,
      [activeTutorialId]: {
        stepIndex: currentStepIndex,
        totalSteps: tutorial?.steps.length || 0,
        status: "skipped",
        updatedAt: new Date().toISOString(),
      },
    };
    persistProgress(next);
    setActiveTutorialId(null);
    setCurrentStepIndex(0);
  }, [activeTutorialId, currentStepIndex, progress, persistProgress]);

  const resetTutorial = useCallback(
    (tutorialId) => {
      const next = { ...progress };
      delete next[tutorialId];
      persistProgress(next);
    },
    [progress, persistProgress]
  );

  const activeTutorial = activeTutorialId ? TUTORIAL_MAP[activeTutorialId] : null;
  const activeStep = activeTutorial ? activeTutorial.steps[currentStepIndex] : null;

  const value = useMemo(
    () => ({
      tutorials: TUTORIALS_LIST,
      progress,
      activeTutorialId,
      activeTutorial,
      activeStep,
      currentStepIndex,
      isRunning: !!activeTutorial,
      getProgress,
      start,
      stop,
      nextStep,
      prevStep,
      goToStep,
      skipTutorial,
      resetTutorial,
    }),
    [
      progress,
      activeTutorialId,
      activeTutorial,
      activeStep,
      currentStepIndex,
      getProgress,
      start,
      stop,
      nextStep,
      prevStep,
      goToStep,
      skipTutorial,
      resetTutorial,
    ]
  );

  return (
    <TutorialContext.Provider value={value}>{children}</TutorialContext.Provider>
  );
}

export function useTutorial() {
  const ctx = useContext(TutorialContext);
  if (!ctx) {
    // Fail-safe: allow components to import even if provider not mounted.
    return {
      tutorials: TUTORIALS_LIST,
      progress: {},
      activeTutorialId: null,
      activeTutorial: null,
      activeStep: null,
      currentStepIndex: 0,
      isRunning: false,
      getProgress: () => null,
      start: () => {},
      stop: () => {},
      nextStep: () => {},
      prevStep: () => {},
      goToStep: () => {},
      skipTutorial: () => {},
      resetTutorial: () => {},
    };
  }
  return ctx;
}
